import React, { Suspense, lazy } from 'react';
import { LoadingSpinner } from '../common';
import { ContinueCTA } from './common';
import { useStackFlow } from '../../context/StackFlowContext';

// Lazy-load each panel
const YieldDiscoveryPanel = lazy(() => import('./panels/YieldDiscoveryPanel'));
const ExchangeRatesPanel = lazy(() => import('./panels/ExchangeRatesPanel'));
const CompliancePanel = lazy(() => import('./panels/CompliancePanel'));
const HedgingPanel = lazy(() => import('./panels/HedgingPanel'));
const LiquidityPanel = lazy(() => import('./panels/LiquidityPanel'));
const ExecutionPanel = lazy(() => import('./panels/ExecutionPanel'));
const PortfolioPanel = lazy(() => import('./panels/PortfolioPanel'));

const PANEL_MAP = {
  yield_discovery: YieldDiscoveryPanel,
  exchange_rates: ExchangeRatesPanel,
  compliance: CompliancePanel,
  hedging: HedgingPanel,
  liquidity: LiquidityPanel,
  execution: ExecutionPanel,
  portfolio: PortfolioPanel,
};

const Fallback = () => (
  <div className="flex justify-center py-16">
    <LoadingSpinner />
  </div>
);

/**
 * StackPanel — renders the lazy-loaded panel for the selected stack element.
 * Props forwarded to child: mode, onResult, onSpinozaHint, openSignUp, openWaitlist.
 * Renders ContinueCTA below panel when step is completed and next exists.
 */
const StackPanel = ({ selected, onNavigate, ...rest }) => {
  const Panel = PANEL_MAP[selected];
  const { completed, getNextPillar } = useStackFlow();

  if (!Panel) return null;

  const isCompleted = completed.has(selected);
  const nextPillar = isCompleted ? getNextPillar(selected) : null;

  return (
    <div
      role="tabpanel"
      id={`stack-panel-${selected}`}
      aria-labelledby={`stack-tab-${selected}`}
    >
      <Suspense fallback={<Fallback />}>
        <Panel {...rest} />
      </Suspense>
      {isCompleted && nextPillar && onNavigate && (
        <ContinueCTA
          nextPillar={nextPillar}
          onSelect={onNavigate}
          completedCount={completed.size}
        />
      )}
    </div>
  );
};

export default StackPanel;
